import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	Signal,
	WritableSignal,
	computed,
	input,
	signal
} from '@angular/core';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

import { AppSetupDb } from 'lib/cosmos/app-setup/appSetupDb.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { IFileFormFieldCropOptions } from 'lib/ui/form/fields/file-form-field.component.js';
import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { IconType } from 'lib/ui/icons/index.js';
import { Observable, map } from 'rxjs';

interface IFeatureFormItem {
	id: string;
	title: WritableSignal<string | undefined>;
	text: WritableSignal<string | undefined>;
	icon: WritableSignal<IconType | undefined>;
	imageMobile: WritableSignal<CosmosDb.ICosmosFileReference[]>;
	imageDesktop: WritableSignal<CosmosDb.ICosmosFileReference[]>;
}

interface ISessionCardFormItem {
	title: string;
	image: WritableSignal<CosmosDb.ICosmosFileReference[]>;
}

@Component({
	selector: 'mod-app-setup-form',
	templateUrl: './app-setup-form.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AppSetupFormComponent extends TypeCoreComponent<AppSetupDb.IAppSetupItem> implements OnInit {
	public readonly isInDialog = input<boolean>(false);

	public readonly textLogoCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'freeform',
		resizeToHeight: 45
	};

	public readonly desktopImageCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'freeform',
		resizeToWidth: 1920
	};
	public readonly mobileImageCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'freeform',
		resizeToWidth: 500
	};

	public readonly logoCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'square',
		resizeToWidth: 250
	};

	public readonly helpdeskCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'square',
		resizeToWidth: 250
	};

	public readonly botCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'square',
		resizeToWidth: 250
	};

	public readonly noDataImageCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'square',
		resizeToWidth: 250
	};

	public readonly sessionImageCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'square',
		resizeToWidth: 500
	};

	public readonly form = {
		logo: signal<CosmosDb.ICosmosFileReference[]>([]),
		textLogoLight: signal<CosmosDb.ICosmosFileReference[]>([]),
		textLogoDark: signal<CosmosDb.ICosmosFileReference[]>([]),
		mainTitle: signal<string | undefined>(undefined),
		mainSubtitle: signal<string | undefined>(undefined),
		price: signal<string | undefined>(undefined),
		appVersion: signal<string | undefined>(undefined),
		mainTeaserDesktop: signal<CosmosDb.ICosmosFileReference[]>([]),
		mainTeaserMobile: signal<CosmosDb.ICosmosFileReference[]>([]),
		ctaTitle: signal<string | undefined>(undefined),
		helpdeskImage: signal<CosmosDb.ICosmosFileReference[]>([]),
		noDataImage: signal<CosmosDb.ICosmosFileReference[]>([]),
		botImage: signal<CosmosDb.ICosmosFileReference[]>([]),
		feature1: {
			title: signal<string | undefined>(undefined),
			text: signal<string | undefined>(undefined),
			icon: signal<IconType | undefined>(undefined),
			imageMobile: signal<CosmosDb.ICosmosFileReference[]>([]),
			imageDesktop: signal<CosmosDb.ICosmosFileReference[]>([])
		},
		feature2: {
			title: signal<string | undefined>(undefined),
			text: signal<string | undefined>(undefined),
			icon: signal<IconType | undefined>(undefined),
			imageMobile: signal<CosmosDb.ICosmosFileReference[]>([]),
			imageDesktop: signal<CosmosDb.ICosmosFileReference[]>([])
		},
		feature3: {
			title: signal<string | undefined>(undefined),
			text: signal<string | undefined>(undefined),
			icon: signal<IconType | undefined>(undefined),
			imageMobile: signal<CosmosDb.ICosmosFileReference[]>([]),
			imageDesktop: signal<CosmosDb.ICosmosFileReference[]>([])
		},
		feature4: {
			title: signal<string | undefined>(undefined),
			text: signal<string | undefined>(undefined),
			icon: signal<IconType | undefined>(undefined),
			imageMobile: signal<CosmosDb.ICosmosFileReference[]>([]),
			imageDesktop: signal<CosmosDb.ICosmosFileReference[]>([])
		},
		sessionCardImages: {
			workout: signal<CosmosDb.ICosmosFileReference[]>([]),
			onlineConsultation: signal<CosmosDb.ICosmosFileReference[]>([]),
			offlineConsultation: signal<CosmosDb.ICosmosFileReference[]>([]),
			inPersonMeeting: signal<CosmosDb.ICosmosFileReference[]>([])
		},
		aiAssistantImage: signal<CosmosDb.ICosmosFileReference[]>([])
	};

	public readonly sessionCardItems: ISessionCardFormItem[] = [
		{
			title: 'Workout',
			image: this.form.sessionCardImages.workout
		},
		{
			title: 'In person meeting',
			image: this.form.sessionCardImages.inPersonMeeting
		},
		{
			title: 'Offline consultation',
			image: this.form.sessionCardImages.offlineConsultation
		},
		{
			title: 'Online consultation',
			image: this.form.sessionCardImages.onlineConsultation
		}
	];

	public readonly featureFormItems: IFeatureFormItem[] = [
		{
			id: 'Feature 1',
			title: this.form.feature1.title,
			text: this.form.feature1.text,
			icon: this.form.feature1.icon,
			imageDesktop: this.form.feature1.imageDesktop,
			imageMobile: this.form.feature1.imageMobile
		},
		{
			id: 'Feature 2',
			title: this.form.feature2.title,
			text: this.form.feature2.text,
			icon: this.form.feature2.icon,
			imageDesktop: this.form.feature2.imageDesktop,
			imageMobile: this.form.feature2.imageMobile
		},
		{
			id: 'Feature 3',
			title: this.form.feature3.title,
			text: this.form.feature3.text,
			icon: this.form.feature3.icon,
			imageDesktop: this.form.feature3.imageDesktop,
			imageMobile: this.form.feature3.imageMobile
		},
		{
			id: 'Feature 4',
			title: this.form.feature4.title,
			text: this.form.feature4.text,
			icon: this.form.feature4.icon,
			imageDesktop: this.form.feature4.imageDesktop,
			imageMobile: this.form.feature4.imageMobile
		}
	];

	public readonly saveHandler = computed<FormSaveHandler>(() => {
		const form = this.form;
		const sharedData = this.sharedData();

		const mainTitle = form.mainTitle();
		const mainSubtitle = form.mainSubtitle();
		const ctaTitle = form.ctaTitle();
		const price = form.price();
		const appVersion = form.appVersion();

		if (!(mainTitle && sharedData && mainSubtitle && ctaTitle && price && appVersion)) {
			return undefined;
		}

		return this.dependencies.httpService
			.patch<AppSetupDb.IAppSetupItem>({
				type: 'appSetup',
				patchModel: {
					id: sharedData.data.id,
					appVersion: appVersion,
					logo: form.logo(),
					textLogoLight: form.textLogoLight(),
					textLogoDark: form.textLogoDark(),
					mainTitle: mainTitle,
					mainSubtitle: mainSubtitle,
					price: price,
					ctaTitle: ctaTitle,
					helpdeskImage: form.helpdeskImage(),
					botImage: form.botImage(),
					mainTeaserDesktop: form.mainTeaserDesktop(),
					mainTeaserMobile: form.mainTeaserMobile(),
					noDataImage: form.noDataImage(),
					sessionCardImages: {
						inPersonMeeting: form.sessionCardImages.inPersonMeeting(),
						offlineConsultation: form.sessionCardImages.offlineConsultation(),
						onlineConsultation: form.sessionCardImages.onlineConsultation(),
						workout: form.sessionCardImages.workout()
					},
					feature1: {
						title: form.feature1.title() ?? '',
						text: form.feature1.text() ?? '',
						icon: form.feature1.icon() ?? 'rocket',
						imageDesktop: form.feature1.imageDesktop(),
						imageMobile: form.feature1.imageMobile()
					},
					feature2: {
						title: form.feature2.title() ?? '',
						text: form.feature2.text() ?? '',
						icon: form.feature2.icon() ?? 'rocket',
						imageDesktop: form.feature2.imageDesktop(),
						imageMobile: form.feature2.imageMobile()
					},
					feature3: {
						title: form.feature3.title() ?? '',
						text: form.feature3.text() ?? '',
						icon: form.feature3.icon() ?? 'rocket',
						imageDesktop: form.feature3.imageDesktop(),
						imageMobile: form.feature3.imageMobile()
					},
					feature4: {
						title: form.feature4.title() ?? '',
						text: form.feature4.text() ?? '',
						icon: form.feature4.icon() ?? 'rocket',
						imageDesktop: form.feature4.imageDesktop(),
						imageMobile: form.feature4.imageMobile()
					},
					aiAssistantImage: form.aiAssistantImage()
				}
			})
			.pipe(
				map((response) => {
					super.notify('Settings updated');
				})
			);
	});

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			this.form.logo.set(sharedData?.data.logo ?? []);
			this.form.textLogoLight.set(sharedData?.data.textLogoLight ?? []);
			this.form.textLogoDark.set(sharedData?.data.textLogoDark ?? []);
			this.form.appVersion.set(sharedData?.data.appVersion);
			this.form.mainTitle.set(sharedData?.data.mainTitle);
			this.form.mainSubtitle.set(sharedData?.data.mainSubtitle);
			this.form.ctaTitle.set(sharedData?.data.ctaTitle);
			this.form.price.set(sharedData?.data.price);
			this.form.helpdeskImage.set(sharedData?.data.helpdeskImage ?? []);
			this.form.botImage.set(sharedData?.data.botImage ?? []);
			this.form.mainTeaserDesktop.set(sharedData?.data.mainTeaserDesktop ?? []);
			this.form.noDataImage.set(sharedData?.data.noDataImage ?? []);
			this.form.mainTeaserMobile.set(sharedData?.data.mainTeaserMobile ?? []);

			this.form.sessionCardImages.workout.set(sharedData?.data.sessionCardImages.workout ?? []);
			this.form.sessionCardImages.inPersonMeeting.set(sharedData?.data.sessionCardImages.inPersonMeeting ?? []);
			this.form.sessionCardImages.offlineConsultation.set(
				sharedData?.data.sessionCardImages.offlineConsultation ?? []
			);
			this.form.sessionCardImages.onlineConsultation.set(
				sharedData?.data.sessionCardImages.onlineConsultation ?? []
			);

			this.form.feature1.title.set(sharedData?.data.feature1.title);
			this.form.feature1.text.set(sharedData?.data.feature1.text);
			this.form.feature1.icon.set(sharedData?.data.feature1.icon);
			this.form.feature1.imageDesktop.set(sharedData?.data.feature1.imageDesktop ?? []);
			this.form.feature1.imageMobile.set(sharedData?.data.feature1.imageMobile ?? []);

			this.form.feature2.title.set(sharedData?.data.feature2.title);
			this.form.feature2.text.set(sharedData?.data.feature2.text);
			this.form.feature2.icon.set(sharedData?.data.feature2.icon);
			this.form.feature2.imageDesktop.set(sharedData?.data.feature2.imageDesktop ?? []);
			this.form.feature2.imageMobile.set(sharedData?.data.feature2.imageMobile ?? []);

			this.form.feature3.title.set(sharedData?.data.feature3.title);
			this.form.feature3.text.set(sharedData?.data.feature3.text);
			this.form.feature3.icon.set(sharedData?.data.feature3.icon);
			this.form.feature3.imageDesktop.set(sharedData?.data.feature3.imageDesktop ?? []);
			this.form.feature3.imageMobile.set(sharedData?.data.feature3.imageMobile ?? []);

			this.form.feature4.title.set(sharedData?.data.feature4.title);
			this.form.feature4.text.set(sharedData?.data.feature4.text);
			this.form.feature4.icon.set(sharedData?.data.feature4.icon);
			this.form.feature4.imageDesktop.set(sharedData?.data.feature4.imageDesktop ?? []);
			this.form.feature4.imageMobile.set(sharedData?.data.feature4.imageMobile ?? []);

			this.form.aiAssistantImage.set(sharedData?.data.aiAssistantImage ?? []);
		};
	}

	getUploadFunction(
		field: Signal<CosmosDb.ICosmosFileReference[]>
	): (file: File) => Observable<CosmosDb.ICosmosFileReference[]> {
		return (file) => {
			return this.dependencies.httpService
				.uploadFile({
					file: file,
					type: 'appSetup',
					clientImageCompression: 'none'
				})
				.pipe(
					map((response) => {
						return [...field(), response.data];
					})
				);
		};
	}

	handleUploadImage(
		files: CosmosDb.ICosmosFileReference[] | undefined,
		field: WritableSignal<CosmosDb.ICosmosFileReference[]>
	): void {
		if (files) {
			field.set(files);
			const saveHandler = this.saveHandler();

			if (saveHandler) {
				super.subscribeToObservable(saveHandler);
			}
		}
	}
}
